import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Tooltip, Select, MenuItem, FormControl, ListItemText } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserSession } from '../contexts/UserContext';
import { useSharePoint } from '../contexts/SharePointContext';
import authApi from '../services/authApi';
import { siteStatsDebug } from '../utils/siteStatsDebug';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FeedbackForm from './common/FeedbackForm';

// Brand colors and constants
const BRAND_COLOR = '#e65100';
const BRAND_COLOR_FADED = '#ff9d57';
const HEADER_HEIGHT = 64;

const IMPERSONATE_ROLES = new Set(['System Admin']);

function normEmail(e) {
  return String(e || '').trim().toLowerCase();
}

/** API / driver may use tenant_url, Tenant_url, etc. */
function rowTenantUrl(row) {
  if (!row) return '';
  const v = row.tenant_url ?? row.Tenant_url ?? row.TenantUrl ?? row.TENANT_URL;
  return v != null ? String(v).trim() : '';
}

/** Hostname from tenant URL for grouping (e.g. fraportusa.sharepoint.com). */
function tenantHostname(tenantUrl) {
  const raw = String(tenantUrl || '').trim();
  if (!raw) return '';
  try {
    const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    return u.hostname.toLowerCase();
  } catch {
    return raw.replace(/^https?:\/\//i, '').split('/')[0].toLowerCase();
  }
}

/** Short label for client row: "fraportusa" from https://fraportusa.sharepoint.com */
function tenantShortLabel(tenantUrl) {
  const host = tenantHostname(tenantUrl);
  if (!host) return '';
  return host.replace(/\.sharepoint\.com$/i, '').replace(/\.onmicrosoft\.com$/i, '') || host;
}

/**
 * One entry per customer (user_email): impersonation uses root site config for that owner.
 */
function groupImpersonationClients(rows) {
  const byEmail = new Map();
  for (const row of rows) {
    const email = normEmail(row.user_email);
    if (!email) continue;
    if (!byEmail.has(email)) byEmail.set(email, []);
    byEmail.get(email).push(row);
  }
  const groups = [];
  for (const [key, list] of byEmail) {
    list.sort((a, b) => {
      const sa = a.is_subsite ? 1 : 0;
      const sb = b.is_subsite ? 1 : 0;
      if (sa !== sb) return sa - sb;
      return (a.config_id || 0) - (b.config_id || 0);
    });
    const root = list[0];
    const tenant = rowTenantUrl(root);
    groups.push({
      key,
      root_config_id: root.config_id,
      user_email: root.user_email,
      site_count: list.length,
      primaryLabel: tenantShortLabel(tenant) || key,
      tenant_host: tenantHostname(tenant),
      rows: list
    });
  }
  groups.sort((a, b) =>
    String(a.primaryLabel).localeCompare(String(b.primaryLabel), undefined, { sensitivity: 'base' })
  );
  return groups;
}

/** Site dropdown: subsite = last path segment only; root = short tenant name. */
function formatSiteSelectLabel(config) {
  if (!config) return '';
  if (config.is_subsite && config.site_path) {
    const parts = String(config.site_path).split('/').filter(Boolean);
    if (parts.length) return parts[parts.length - 1];
    return config.site_path;
  }
  const t = config.tenant_url ? String(config.tenant_url) : '';
  return tenantShortLabel(t) || 'Main site';
}

/** One row per customer (SharePoint owner), not per subsite config. */
function CustomerAccountSelect({ impersonatedEmail, impersonatedConfigId, onSelectConfig }) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    authApi.getImpersonationOptions('').then((rows) => {
      if (!cancelled) setOptions(Array.isArray(rows) ? rows : []);
    }).catch(() => {
      if (!cancelled) setOptions([]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientGroups = useMemo(() => groupImpersonationClients(options), [options]);

  const selectValue = useMemo(() => {
    if (!impersonatedEmail && (impersonatedConfigId == null || impersonatedConfigId === '')) {
      return '__self__';
    }
    if (impersonatedConfigId != null && impersonatedConfigId !== '') {
      const id = parseInt(impersonatedConfigId, 10);
      if (!Number.isNaN(id) && id > 0) {
        const row = options.find((r) => r.config_id === id);
        if (row && row.user_email) return `client:${normEmail(row.user_email)}`;
        return `cfg:${id}`;
      }
    }
    const match = options.find((r) => normEmail(r.user_email) === normEmail(impersonatedEmail));
    if (match && match.user_email) return `client:${normEmail(match.user_email)}`;
    return '__self__';
  }, [impersonatedEmail, impersonatedConfigId, options]);

  const renderSelected = (selected) => {
    if (selected === '__self__') return 'Your account';
    if (String(selected).startsWith('client:')) {
      const key = String(selected).slice(7);
      const g = clientGroups.find((c) => c.key === key);
      return g ? g.primaryLabel : 'Client';
    }
    const id = parseInt(String(selected).replace(/^cfg:/, ''), 10);
    if (!Number.isNaN(id)) {
      const row = options.find((r) => r.config_id === id);
      if (row && row.user_email) {
        const g = clientGroups.find((c) => c.key === normEmail(row.user_email));
        if (g) return g.primaryLabel;
      }
      if (row) {
        const tenant = rowTenantUrl(row);
        const short = tenant.length > 42 ? `${tenant.slice(0, 39)}…` : tenant;
        return short || row.user_email || `Config #${id}`;
      }
    }
    return 'Your account';
  };

  return (
    <Box
      sx={{
        mr: 1,
        minWidth: 200,
        maxWidth: 320,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="caption"
        component="label"
        htmlFor="header-client-select"
        sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.25, pl: 0.25, fontWeight: 600, lineHeight: 1.2 }}
      >
        Client
      </Typography>
      <FormControl
        size="small"
        sx={{
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          borderRadius: 1,
          '& .MuiOutlinedInput-root': {
            color: 'white',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
            '&.Mui-focused fieldset': { borderColor: 'white' },
          },
          '& .MuiSvgIcon-root': { color: 'white' },
        }}
      >
        <Select
          id="header-client-select"
          inputProps={{ 'aria-label': 'Client' }}
          value={selectValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '__self__') onSelectConfig(null);
            else if (String(v).startsWith('client:')) {
              const key = String(v).slice(7);
              const g = clientGroups.find((c) => c.key === key);
              if (g) onSelectConfig(g.root_config_id);
            } else {
              const id = parseInt(String(v).replace(/^cfg:/, ''), 10);
              onSelectConfig(Number.isNaN(id) ? null : id);
            }
          }}
          displayEmpty
          renderValue={renderSelected}
          MenuProps={{ PaperProps: { sx: { maxHeight: 420, maxWidth: 560 } } }}
        >
          <MenuItem value="__self__">Your account</MenuItem>
          {clientGroups.map((g) => {
            const secondaryParts = [];
            const rootRow = g.rows[0];
            const name = [rootRow.first_name, rootRow.last_name].filter(Boolean).join(' ').trim();
            if (name) secondaryParts.push(name);
            if (g.user_email) secondaryParts.push(g.user_email);
            if (g.site_count > 1) secondaryParts.push(`${g.site_count} sites`);
            const secondary = secondaryParts.join(' · ') || '—';
            return (
              <MenuItem
                key={g.key}
                value={`client:${g.key}`}
                sx={{ alignItems: 'flex-start', py: 1 }}
              >
                <ListItemText
                  primary={g.primaryLabel}
                  secondary={secondary}
                  primaryTypographyProps={{ noWrap: false, sx: { whiteSpace: 'normal' } }}
                  secondaryTypographyProps={{
                    noWrap: false,
                    sx: { whiteSpace: 'normal', fontSize: '0.75rem', opacity: 0.9 },
                  }}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
}

function Header() {
  const { isAuthenticated, logout, refreshUser } = useAuth();
  const { userSession } = useUserSession();
  const { configurations, selectedConfig, effectiveConfigId, syncSelectedConfig, changeSelectedConfig, bumpConfigRefresh } = useSharePoint();
  const navigate = useNavigate();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    logout();
  };

  const handleLoginClick = () => {
    navigate('/login', { state: { redirectAfterLogin: window.location.pathname } });
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleOpenFeedback = () => {
    setFeedbackOpen(true);
  };

  const handleCloseFeedback = () => {
    setFeedbackOpen(false);
  };

  const roles = userSession?.roles || [];
  const canImpersonate = roles.some((r) => IMPERSONATE_ROLES.has(r));

  const handleConfigChange = async (event) => {
    const raw = event.target.value;
    const configId = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (Number.isNaN(configId) || configId < 1) return;

    siteStatsDebug('SiteSwitch', {
      event: 'site_dropdown_change',
      oldSelectedConfig: selectedConfig,
      newConfigId: configId,
      impersonating: Boolean(userSession?.impersonatedEmail),
      impersonatedConfigId: userSession?.impersonatedConfigId ?? null,
      path: canImpersonate && userSession?.impersonatedEmail ? 'impersonate_soft_refresh' : 'changeSelectedConfig_reload',
    });

    /** While impersonating, JWT must carry the chosen SharePoint row or APIs 403 on stale root id. */
    if (canImpersonate && userSession?.impersonatedEmail) {
      syncSelectedConfig(configId);
      try {
        await authApi.impersonate(configId);
        const me = await refreshUser();
        siteStatsDebug('SiteSwitch', {
          event: 'site_dropdown_after_impersonate',
          newConfigId: configId,
          impersonatedConfigIdAfterRefresh: me?.impersonatedConfigId ?? null,
          effectiveConfigId: me?.impersonatedConfigId ?? configId,
        });
        bumpConfigRefresh(configId);
      } catch (e) {
        console.error(e);
      }
      return;
    }

    changeSelectedConfig(configId);
  };

  const handleImpersonateTarget = useCallback(async (configIdOrNull) => {
    siteStatsDebug('ClientSwitch', {
      event: configIdOrNull == null ? 'impersonate_clear' : 'client_impersonate',
      oldSelectedConfig: selectedConfig,
      newConfigId: configIdOrNull,
      impersonatedConfigId: userSession?.impersonatedConfigId ?? null,
    });
    try {
      if (configIdOrNull == null) {
        await authApi.impersonateClear();
        await refreshUser();
        bumpConfigRefresh();
      } else {
        await authApi.impersonate(configIdOrNull);
        await refreshUser();
        syncSelectedConfig(configIdOrNull);
        bumpConfigRefresh(configIdOrNull);
      }
    } catch (e) {
      console.error(e);
    }
  }, [refreshUser, bumpConfigRefresh, syncSelectedConfig, selectedConfig, userSession?.impersonatedConfigId]);

  const welcomeName = (() => {
    const s = userSession;
    if (!s) return 'Guest';
    const fn = (s.first_name || '').trim();
    const ln = (s.last_name || '').trim();
    if (fn && ln) return `${fn} ${ln}`;
    const dn = (s.displayName || '').trim();
    if (dn) return dn;
    if (fn) return fn;
    if (s.email) return s.email;
    return 'Guest';
  })();

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={2}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: BRAND_COLOR,
          height: HEADER_HEIGHT,
          width: '100%',
          position: 'fixed',
          top: 0
        }}
      >
        <Toolbar sx={{ height: '100%' }}>
          <Typography 
            variant="h6" 
            component="div"
            sx={{ 
              flexGrow: 1, 
              cursor: 'pointer',
              fontWeight: 700,
              letterSpacing: '0.5px',
              transition: 'opacity 0.2s ease-in-out',
              '&:hover': {
                opacity: 0.9,
              },
              fontSize: {
                xs: '1rem',
                sm: '1.25rem',
              },
            }} 
            onClick={handleLogoClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleLogoClick()}
          >
            Vertilocity SecurePoint IQ
          </Typography>
          
          {isAuthenticated && userSession ? (
            <>
              {canImpersonate && (
                <CustomerAccountSelect
                  impersonatedEmail={userSession.impersonatedEmail}
                  impersonatedConfigId={userSession.impersonatedConfigId}
                  onSelectConfig={handleImpersonateTarget}
                />
              )}
              {configurations && configurations.length > 1 && (
                <Box
                  sx={{
                    flexGrow: 1,
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    minWidth: 180,
                    maxWidth: 400,
                    paddingTop: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="header-site-select"
                    sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 0.25, pl: 0.25, fontWeight: 600, lineHeight: 1.2 }}
                  >
                    Site
                  </Typography>
                  <FormControl
                    size="small"
                    fullWidth
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: 1,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'white',
                        },
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    }}
                  >
                    <Select
                      id="header-site-select"
                      inputProps={{ 'aria-label': 'Site' }}
                      value={effectiveConfigId || ''}
                      onChange={handleConfigChange}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Select site</span>;
                        }
                        const config = configurations.find((c) => c.id === selected);
                        if (!config) return selected;
                        const label = formatSiteSelectLabel(config);
                        return label.length > 40 ? `${label.slice(0, 37)}…` : label;
                      }}
                      fullWidth
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 320,
                            minWidth: 220,
                            zIndex: 9999,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        disableScrollLock: true,
                      }}
                    >
                      {configurations.map((config) => {
                        const label = formatSiteSelectLabel(config);
                        const tip = [config.tenant_url, config.site_path].filter(Boolean).join(' — ');
                        return (
                          <MenuItem
                            key={config.id}
                            value={config.id}
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}
                            title={tip || config.tenant_url}
                          >
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 2,
                  height: '100%',
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#FFFFFF',
                    display: { xs: 'none', md: 'block' },
                    fontSize: '0.9rem',
                  }}
                >
                  Welcome, {welcomeName}: {currentDateTime.toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </Typography>

                <Tooltip title="Share Feedback">
                  <IconButton
                    color="inherit"
                    onClick={handleOpenFeedback}
                    sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
                  >
                    <FeedbackIcon />
                  </IconButton>
                </Tooltip>

                <Button 
                  variant="outlined"
                  color="inherit" 
                  onClick={handleLogout}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'none',
                    px: 2,
                    py: 0.75,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: '#FFFFFF',
                    },
                  }}
                >
                  Logout
                </Button>
              </Box>
            </>
          ) : (
            <Button 
              variant="outlined"
              color="inherit" 
              onClick={handleLoginClick}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'none',
                px: 3,
                py: 0.75,
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#FFFFFF',
                },
              }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Feedback Form Dialog */}
      <FeedbackForm open={feedbackOpen} onClose={handleCloseFeedback} />
    </>
  );
}

export default Header;